// Curated catalog of purposes / technologies / collected data per service.
// Borlabs does not store these (they are eRecht24/Usercentrics catalog data), so
// we maintain them here — once, for all sites. Keyed by Borlabs service key.
// Per-site fields (company, privacy URL, description) come from Borlabs and are
// merged in at render time; this only supplies the standardized lists.

interface CatalogText {
  description: string;
  purposes: string[];
  technologies: string[];
  data: string[];
}

type CatalogEntry = { de: CatalogText; en: CatalogText };

// Aliases: different Borlabs keys that share the same catalog entry.
const ALIASES: Record<string, string> = {
  'meta-pixel-for-wordpress': 'facebook',
  'facebook-pixel': 'facebook',
  meta: 'facebook',
  'google-maps': 'maps',
  'linkedin-insight': 'linkedin',
};

const CATALOG: Record<string, CatalogEntry> = {
  'google-analytics': {
    de: {
      description: 'Webanalysedienst von Google zur Auswertung des Nutzerverhaltens und zur Reichweitenmessung.',
      purposes: ['Analyse', 'Statistik', 'Reichweitenmessung'],
      technologies: ['Cookies', 'Pixel'],
      data: ['IP-Adresse', 'Geräteinformationen', 'Browser-Informationen', 'Besuchte Seiten', 'Datum und Uhrzeit des Besuchs'],
    },
    en: {
      description: 'Google web analytics service for evaluating user behaviour and measuring reach.',
      purposes: ['Analytics', 'Statistics', 'Audience measurement'],
      technologies: ['Cookies', 'Pixel'],
      data: ['IP address', 'Device information', 'Browser information', 'Pages visited', 'Date and time of visit'],
    },
  },
  'google-ads': {
    de: {
      description: 'Werbedienst von Google. Ermöglicht personalisierte oder nicht personalisierte Werbung sowie Conversion-Tracking.',
      purposes: ['Werbung', 'Analyse', 'Bereitstellung von Diensten', 'Statistiken'],
      technologies: ['Cookies', 'Tracking Pixel'],
      data: ['Angesehene Werbeanzeigen', 'Cookie ID', 'Datum und Uhrzeit des Besuchs', 'Geräteinformationen', 'Geografischer Standort', 'IP-Adresse', 'Suchbegriffe', 'Impressionen', 'Online-Identifikatoren', 'Browser-Informationen'],
    },
    en: {
      description: 'Advertising service by Google. Enables personalised or non-personalised ads and conversion tracking.',
      purposes: ['Advertising', 'Analytics', 'Service provision', 'Statistics'],
      technologies: ['Cookies', 'Tracking Pixel'],
      data: ['Viewed ads', 'Cookie ID', 'Date and time of visit', 'Device information', 'Geographic location', 'IP address', 'Search terms', 'Impressions', 'Online identifiers', 'Browser information'],
    },
  },
  'google-tag-manager': {
    de: {
      description: 'Verwaltet und steuert Tracking-Tags zentral, ohne den Seiten-Code zu ändern.',
      purposes: ['Verwaltung von Tracking-Tags', 'Ausspielung von Skripten'],
      technologies: ['JavaScript'],
      data: ['Aggregierte Daten zur Tag-Auslösung'],
    },
    en: {
      description: 'Centrally manages and controls tracking tags without changing the site code.',
      purposes: ['Tag management', 'Script delivery'],
      technologies: ['JavaScript'],
      data: ['Aggregated tag-firing data'],
    },
  },
  facebook: {
    de: {
      description: 'Dienst von Meta für Werbung, Reichweitenmessung und Conversion-Tracking.',
      purposes: ['Werbung', 'Analyse', 'Reichweitenmessung'],
      technologies: ['Cookies', 'Tracking Pixel'],
      data: ['Cookie ID', 'IP-Adresse', 'Geräteinformationen', 'Nutzungsdaten', 'Browser-Informationen'],
    },
    en: {
      description: 'Meta service for advertising, reach measurement and conversion tracking.',
      purposes: ['Advertising', 'Analytics', 'Audience measurement'],
      technologies: ['Cookies', 'Tracking Pixel'],
      data: ['Cookie ID', 'IP address', 'Device information', 'Usage data', 'Browser information'],
    },
  },
  youtube: {
    de: {
      description: 'Videoplattform von Google zum Einbetten und Abspielen von Videos.',
      purposes: ['Anzeige von Videoinhalten'],
      technologies: ['Cookies', 'Eingebettete Inhalte'],
      data: ['IP-Adresse', 'Geräteinformationen', 'Angesehene Videos', 'Browser-Informationen'],
    },
    en: {
      description: 'Google video platform for embedding and playing videos.',
      purposes: ['Display of video content'],
      technologies: ['Cookies', 'Embedded content'],
      data: ['IP address', 'Device information', 'Videos viewed', 'Browser information'],
    },
  },
  vimeo: {
    de: {
      description: 'Videoplattform zum Einbetten und Abspielen von Videos.',
      purposes: ['Anzeige von Videoinhalten'],
      technologies: ['Cookies', 'Eingebettete Inhalte'],
      data: ['IP-Adresse', 'Geräteinformationen', 'Angesehene Videos', 'Browser-Informationen'],
    },
    en: {
      description: 'Video platform for embedding and playing videos.',
      purposes: ['Display of video content'],
      technologies: ['Cookies', 'Embedded content'],
      data: ['IP address', 'Device information', 'Videos viewed', 'Browser information'],
    },
  },
  maps: {
    de: {
      description: 'Kartendienst von Google zur Anzeige interaktiver Karten.',
      purposes: ['Anzeige von Karteninhalten', 'Standortdienste'],
      technologies: ['Cookies', 'Eingebettete Inhalte'],
      data: ['IP-Adresse', 'Standortdaten', 'Geräteinformationen', 'Browser-Informationen'],
    },
    en: {
      description: 'Google mapping service for displaying interactive maps.',
      purposes: ['Display of map content', 'Location services'],
      technologies: ['Cookies', 'Embedded content'],
      data: ['IP address', 'Location data', 'Device information', 'Browser information'],
    },
  },
  recaptcha: {
    de: {
      description: 'Dienst von Google zum Schutz vor Spam und automatisierten Zugriffen (Bots).',
      purposes: ['Spam-Schutz', 'Bot-Erkennung', 'Sicherheit'],
      technologies: ['Cookies', 'JavaScript'],
      data: ['IP-Adresse', 'Geräteinformationen', 'Mausbewegungen', 'Browser-Informationen'],
    },
    en: {
      description: 'Google service protecting against spam and automated access (bots).',
      purposes: ['Spam protection', 'Bot detection', 'Security'],
      technologies: ['Cookies', 'JavaScript'],
      data: ['IP address', 'Device information', 'Mouse movements', 'Browser information'],
    },
  },
  linkedin: {
    de: {
      description: 'Dienst von LinkedIn für Werbung, Analyse und Conversion-Tracking.',
      purposes: ['Werbung', 'Conversion-Tracking', 'Analyse'],
      technologies: ['Cookies', 'Tracking Pixel'],
      data: ['Cookie ID', 'IP-Adresse', 'Geräteinformationen', 'Berufliche Informationen'],
    },
    en: {
      description: 'LinkedIn service for advertising, analytics and conversion tracking.',
      purposes: ['Advertising', 'Conversion tracking', 'Analytics'],
      technologies: ['Cookies', 'Tracking Pixel'],
      data: ['Cookie ID', 'IP address', 'Device information', 'Professional information'],
    },
  },
};

/** Catalog purposes/technologies/data for a Borlabs service key, or null. */
export function catalogFor(key: string, lang: 'de' | 'en'): CatalogText | null {
  const resolved = ALIASES[key] ?? key;
  const entry = CATALOG[resolved];
  return entry ? entry[lang] : null;
}
